import axios from 'axios';
import { setupAxiosInterceptor } from './auth/axiosConfig';
import config from './config';

// Create an API instance
const userApi = axios.create({
  baseURL: config.API_BASE_URL + '/cellcog/user',
});

setupAxiosInterceptor(userApi);

/**
 * Get the user's CellCog access status
 * @returns {Promise} Response with access status information
 */
export const getCellCogStatus = () => {
  return userApi.get('/cellcog-status');
};

/**
 * Request access to CellCog
 * @param {string} reason - The reason for requesting access
 * @returns {Promise} Response with request status
 */
export const requestCellCogAccess = (reason) => {
  return userApi.post('/register', { reason });
};

/**
 * Persist the cookie-consent choice on the ACCOUNT (geo-split v3, CEL-1678):
 * localStorage is per-browser; this makes a logged-in user's choice follow them
 * to every device. Fire-and-forget-safe (last-write-wins server-side). Rejects
 * 403 during admin spoof sessions by design — callers treat that as a no-op.
 * @param {boolean} analytics - analytics consent granted
 * @param {string} source - 'banner' | 'settings' | 'gpc'
 */
export const updateCookieConsent = (analytics, source) => {
  return userApi.put('/cookie-consent', { analytics, source });
};

/**
 * Get the user's profile
 * Checks for referral_code in sessionStorage and passes it to backend for new user attribution
 * @returns {Promise} Response with user profile
 */
export const getUserProfile = () => {
  // Check for referral code in sessionStorage (set during invitation link signup)
  const referralCode = sessionStorage.getItem('referral_code');

  // Get client-side detected country from geoDetection cache (fallback for local dev where GCP LB header is absent)
  let detectedCountry = null;
  try {
    const geoCacheRaw = localStorage.getItem('cellcog_geo_cache');
    if (geoCacheRaw) {
      const geoCache = JSON.parse(geoCacheRaw);
      if (geoCache.country && geoCache.country.length === 2) {
        detectedCountry = geoCache.country;
      }
    }
  } catch { /* ignore parse errors */ }

  const params = {};
  if (referralCode) params.referral_code = referralCode;
  if (detectedCountry) params.detected_country = detectedCountry;

  // Report the browser's IANA timezone — the single source of truth for User.timezone.
  // Sent on every /profile call so travel/OS timezone changes self-heal (last-write-wins).
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) params.tz = tz;
  } catch { /* ignore */ }

  // "Languages I read" (translation v2, Nov 2 2026): the browser's languages, which the server uses ONCE to fill the list of an
  // account that never chose (never during an admin spoofing session, never over a chosen list). Normalized server-side.
  try {
    const langs = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language])
      .filter((l) => typeof l === 'string' && l).slice(0, 20).join(',').slice(0, 400);
    if (langs) params.langs = langs;
  } catch { /* ignore */ }

  const request = userApi.get('/profile', { params });

  if (referralCode) {
    return request.then(response => {
      sessionStorage.removeItem('referral_code');
      return response;
    });
  }

  return request;
};

export const updateUserProfile = newProfile => {
  return userApi.put('/update', newProfile);
};

/**
 * Confirm email verification after applyActionCode succeeds.
 * Backend double-checks with Firebase Admin SDK before enabling.
 * Moves user from SIGNED_UP → EMAIL_VERIFIED.
 * @returns {Promise} Response with onboarding_status
 */
export const verifyEmail = () => {
  return userApi.post('/verify-email');
};

/**
 * Complete the consent stepper.
 * Moves user from EMAIL_VERIFIED → JOINED.
 * @returns {Promise} Response with onboarding_status
 */
export const completeConsent = (payload = {}) => {
  // payload: { email_optin: bool, form_version: string } (CEL-2355 consent record); empty = legacy behaviour.
  return userApi.post('/complete-consent', payload);
};

// "Working with a team?" (Oct 2 2026): choice = 'create' | 'personal'. Also the Switch window's later domain claim.
export const postTeamChoice = (choice) => {
  return userApi.post('/team-choice', { choice });
};

// ============= API Key Management (multi-key) =============

/**
 * List all active API keys for the current user.
 * Response: { keys: [{id, name, key_prefix, created_at, last_used_at, is_active}], active_count, active_cap }
 */
export const listApiKeys = () => {
  return userApi.get('/api-keys');
};

/**
 * Create a new named API key. Returns the plaintext key ONCE — client must save it.
 * @param {string} name - User-visible label (1-64 chars)
 * Response: { id, name, api_key (PLAINTEXT), key_prefix, created_at }
 */
export const createApiKey = (name) => {
  return userApi.post('/api-keys', { name });
};

/**
 * Rename an existing API key.
 * @param {string} keyId - ApiKey document id
 * @param {string} name - New name
 */
export const renameApiKey = (keyId, name) => {
  return userApi.patch(`/api-keys/${keyId}`, { name });
};

/**
 * Revoke (soft-delete) an API key by id. Idempotent.
 * @param {string} keyId - ApiKey document id
 */
export const revokeApiKeyById = (keyId) => {
  return userApi.delete(`/api-keys/${keyId}`);
};

/**
 * Rotate a platform-managed key (the Subagents key) by id: a new key replaces it at once.
 * Response: { id, name, key_prefix, created_at, managed_by, message } (masked only; the value is never returned).
 * @param {string} keyId - ApiKey document id
 */
export const rotateApiKeyById = (keyId) => {
  return userApi.post(`/api-keys/${keyId}/rotate`);
};


// ============= Activation Fork (CEL-1341) =============

/**
 * Record the activation-fork impression (the gate rendered for this user).
 * Show-once is impression-based; idempotent server-side. Fired on first
 * render — never in preview mode (?fork_preview=1).
 */
/** One delayed retry for the best-effort fork stamps (CEL-1814): these writes
 *  are fire-and-forget and demonstrably drop on flaky first-session networks
 *  (users whose first chat message proves a submit, but whose fork record
 *  reads dropped). A single 2s retry makes the fork-state data honest without
 *  ever blocking onboarding — callers still .catch, and the server writes are
 *  idempotent (submit/skip first-write-wins, door/chip last-write-wins), so a
 *  duplicate landing is harmless. */
const postForkStampWithRetry = (path, body = undefined) =>
  userApi.post(path, body).catch(
    () => new Promise((r) => setTimeout(r, 2000)).then(() => userApi.post(path, body)),
  );

// Web Push (CEL-2450): public key is null when the server has no VAPID keys (feature hidden).
export const getPushPublicKey = () => userApi.get('/push/public-key');
export const savePushSubscription = (subscription) => userApi.post('/push/subscriptions', { subscription });
export const removePushSubscription = (endpoint) => userApi.delete('/push/subscriptions', { params: { endpoint } });

export const markActivationForkShown = () => {
  return postForkStampWithRetry('/activation-fork-shown');
};

/** Wait cards (Sep 25 2026): "shown" answers {show} for this hire and counts it; "dismissed" records Skip/X. */
export const postWaitCardsHireEvent = (employeeId, event) =>
  userApi.post('/wait-cards/hire', { employee_id: employeeId, event });
/** Wait cards, chat moment: "shown" answers {show} for this chat and counts it; "dismissed" records X. */
export const postWaitCardsChatEvent = (chatId, event) =>
  userApi.post('/wait-cards/chat', { chat_id: chatId, event });

/**
 * Record a fork outcome event server-side (CEL-1462): door_pick | chip_pick | submit | skip.
 * Mirrors the PostHog events onto the User document (PostHog sessions are anonymous, so
 * nothing there joins a door to a person). ALWAYS fire-and-forget — callers must .catch
 * and never await this in the onboarding path.
 */
export const recordActivationForkEvent = (event, door = null, chip = null) => {
  return postForkStampWithRetry('/activation-fork-event', { event, door, chip });
};


// ============= Feature ToS Acceptance =============

/**
 * Accept feature-specific Terms of Service
 * @param {string} feature - 'drive' or 'cowork'
 * @returns {Promise} Response with acceptance confirmation
 */
export const acceptFeatureTos = (feature) => {
  return userApi.post('/accept-feature-tos', { feature });
};

// ============= Address Removal =============

/**
 * Remove the stored postal address (settings redesign 2026-07-27).
 * Server guards: personal accounts with an active sending domain are refused (400 with detail).
 */
export const removeUserAddress = () => {
  return userApi.delete('/address');
};

// ============= Account Deletion =============

/**
 * Delete the current user's account permanently.
 * Requires exact confirmation string "DELETE MY ACCOUNT".
 * @returns {Promise} Response with deletion confirmation
 */
export const deleteAccount = () => {
  return userApi.delete('/delete-account', {
    data: { confirmation: 'DELETE MY ACCOUNT' },
  });
};
